/* ==========================================================================
   hp-header.js - behaviour for sections/hp-header.liquid (Figma 183:34529).
   ES2018, no dependencies, defer-safe, idempotent. Uses window.hp (hp-base.js)
   for the search overlay + drawer modals ([data-hp-modal-open] wiring is
   delegated there). This file adds:
     1. sticky collapse: slides the incentives bar away after 80px of scroll
        (same behaviour as the previous header's top bar) and exposes
        --hp-header-height (full) + --hp-header-sticky-height (visible when
        collapsed, consumed by html { scroll-padding-top }) on <html>;
        menu-fit guard: .hp-header--menu-overflow swaps the centred desktop
        menu for the hamburger when it would overprint the logo/actions
     2. desktop dropdown / mega menus (hover + click, keyboard, outside click);
        simple popovers flip to right-anchored when they would leave the viewport
     3. drawer accordions, drawer search -> search overlay, drawer cart -> close;
        html.hp-header-modal-open while the search overlay / drawer is open
        (raises the sticky wrapper above legacy fixed bars, hides the chat launcher)
     4. "Exact match" decoration of the theme's predictive-search results, the
        combobox keyboard pattern (options, ArrowUp/Down, Enter, aria-activedescendant)
        and a role=status live region ([data-hp-results-status]) mirroring the result
        group headings ("Products (5), Collections (2)") since predictive-search.js is silent
     5. cart count sync ([data-hp-cart-count]) from cart:updated / cart:item-added
   ========================================================================== */
(function (win, doc) {
  'use strict';

  if (win.__hpHeaderBoot) { return; }
  win.__hpHeaderBoot = true;

  var desktopMQ = typeof win.matchMedia === 'function' ? win.matchMedia('(min-width: 1024px)') : null;
  var hoverMQ = typeof win.matchMedia === 'function' ? win.matchMedia('(hover: hover)') : null;

  function isDesktop() { return !!(desktopMQ && desktopMQ.matches); }
  function canHover() { return !!(hoverMQ && hoverMQ.matches); }
  function hp() { return win.hp && win.hp.__hpBase ? win.hp : null; }
  function ready(fn) {
    if (doc.readyState === 'loading') { doc.addEventListener('DOMContentLoaded', fn, { once: true }); } else { fn(); }
  }
  function onMQChange(mq, fn) {
    if (!mq) { return; }
    if (typeof mq.addEventListener === 'function') { mq.addEventListener('change', fn); } else if (typeof mq.addListener === 'function') { mq.addListener(fn); }
  }
  function norm(s) { return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim(); }
  /* separator-free form for part-number comparisons (A06B6079H106 vs A06B-6079-H106) */
  function compact(s) { return norm(s).replace(/[\s\-_.\/]+/g, ''); }

  function initHeader(root) {
    if (!root || root.__hpHeader) { return; }
    root.__hpHeader = true;

    var wrapper = root.closest('.shopify-section') || root.parentElement;
    var incentives = root.querySelector('[data-hp-header-incentives]');
    var searchModal = root.querySelector('[data-hp-search]');
    var drawer = root.querySelector('[data-hp-drawer]');
    var burger = root.querySelector('[data-hp-burger]');

    /* ---------------------------------------------------------- 1. sticky */
    var sticky = root.getAttribute('data-sticky') === 'true';
    var collapse = root.getAttribute('data-collapse') === 'true';
    var raf = false;
    var focusInside = false; /* keyboard focus is inside the incentives bar (a linked incentive) */
    function setHeightVar() {
      var full = root.offsetHeight;
      var visible = full;
      if (sticky && collapse && incentives) { visible -= incentives.offsetHeight; }
      doc.documentElement.style.setProperty('--hp-header-height', full + 'px');
      doc.documentElement.style.setProperty('--hp-header-sticky-height', visible + 'px');
    }
    function onScroll() {
      raf = false;
      if (!sticky || !collapse || !incentives || !wrapper) { return; }
      /* A focused control inside the collapsed bar would sit off screen (WCAG 2.4.11), so the bar
         stays open while focus is inside it, whatever the scroll position. */
      if (focusInside) { wrapper.style.top = '0px'; return; }
      var y = win.pageYOffset || doc.documentElement.scrollTop || 0;
      if (y > 80) {
        wrapper.style.top = (-incentives.offsetHeight) + 'px';
      } else if (y < 40) {
        wrapper.style.top = '0px';
      }
    }
    /* Scroll path: hp-motion writes styles every frame, so reading layout here would force a style
       flush on each scroll frame; re-measure only when the sticky wrapper's offset actually changed. */
    function schedule() {
      if (raf) { return; }
      raf = true;
      win.requestAnimationFrame(function () {
        var before = wrapper ? wrapper.style.top : '';
        onScroll();
        if (wrapper && wrapper.style.top !== before) { setHeightVar(); }
      });
    }
    /* The desktop menu is absolutely centred on the bar (Figma), so nothing stops long labels or
       extra items from overprinting the logo and the account/search/cart buttons. When the list
       is wider than the gap between them, hide it and show the hamburger (drawer) instead. */
    var menuEl = root.querySelector('.hp-header__menu');
    var menuList = root.querySelector('.hp-header__menu-list');
    var logoEl = root.querySelector('.hp-header__logo');
    var actionsEl = root.querySelector('.hp-header__actions');
    function checkMenuFit() {
      if (!menuEl || !menuList || !logoEl || !actionsEl) { return; }
      var over = false;
      if (isDesktop()) {
        root.classList.remove('hp-header--menu-overflow');
        var avail = actionsEl.getBoundingClientRect().left - logoEl.getBoundingClientRect().right - 32;
        over = menuList.scrollWidth > avail;
      }
      root.classList.toggle('hp-header--menu-overflow', over);
    }
    /* Resize / load path: menu fit depends on the width, the height vars on the full layout. */
    var resizeRaf = false;
    function scheduleResize() {
      if (resizeRaf) { return; }
      resizeRaf = true;
      win.requestAnimationFrame(function () { resizeRaf = false; onScroll(); checkMenuFit(); setHeightVar(); });
    }
    checkMenuFit();
    setHeightVar();
    if (sticky && collapse && incentives && wrapper) {
      win.addEventListener('scroll', schedule, { passive: true });
      /* Focus entering the bar (Tab onto the Google pill) opens it at once; focus leaving it lets
         the next frame apply the scroll position again. */
      incentives.addEventListener('focusin', function () {
        focusInside = true;
        if (wrapper.style.top !== '0px') { wrapper.style.top = '0px'; setHeightVar(); }
      });
      incentives.addEventListener('focusout', function () {
        focusInside = false;
        schedule();
      });
      onScroll();
    }
    win.addEventListener('resize', scheduleResize, { passive: true });
    onMQChange(desktopMQ, checkMenuFit);
    /* Geist loads async; label widths change once it arrives */
    if (doc.fonts && doc.fonts.ready && typeof doc.fonts.ready.then === 'function') {
      doc.fonts.ready.then(function () { checkMenuFit(); setHeightVar(); }).catch(function () { /* noop */ });
    }

    /* ----------------------------------------------------- 2. desktop menu */
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-hp-menu-item]'));
    var openItem = null;
    var closeTimer = null;

    function panelOf(item) { return item.querySelector('[data-hp-menu-panel]'); }
    function toggleOf(item) { return item.querySelector('[data-hp-menu-toggle]'); }

    function setOpen(item, open) {
      var btn = toggleOf(item);
      var panel = panelOf(item);
      if (!btn || !panel) { return; }
      if (open) {
        if (openItem && openItem !== item) { setOpen(openItem, false); }
        panel.hidden = false;
        /* simple popovers are left-anchored (left:-2px, min-width 320); flip the last ones to the
           right edge of their item when they would run past the viewport (e.g. "Services" at 1024) */
        panel.classList.remove('hp-header__panel--flip');
        if (!item.classList.contains('hp-header__menu-item--mega') &&
            !item.classList.contains('hp-header__menu-item--sections') &&
            panel.getBoundingClientRect().right > doc.documentElement.clientWidth - 16) {
          panel.classList.add('hp-header__panel--flip');
        }
        btn.setAttribute('aria-expanded', 'true');
        item.classList.add('is-open');
        openItem = item;
      } else {
        panel.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-open');
        item.__hpHoverOpened = false;
        if (openItem === item) { openItem = null; }
      }
    }
    function closeAll() { if (openItem) { setOpen(openItem, false); } }

    items.forEach(function (item) {
      var btn = toggleOf(item);
      var panel = panelOf(item);
      if (!btn || !panel) { return; }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (panel.hidden) {
          setOpen(item, true);
        } else if (item.__hpHoverOpened) {
          /* opened by hover a moment ago: a click keeps it open instead of closing it */
          item.__hpHoverOpened = false;
        } else {
          setOpen(item, false);
        }
      });
      item.addEventListener('mouseenter', function () {
        if (!isDesktop() || !canHover()) { return; }
        win.clearTimeout(closeTimer);
        if (panel.hidden) {
          setOpen(item, true);
          item.__hpHoverOpened = true;
        }
      });
      item.addEventListener('mouseleave', function () {
        if (!isDesktop() || !canHover()) { return; }
        win.clearTimeout(closeTimer);
        closeTimer = win.setTimeout(function () { if (openItem === item) { setOpen(item, false); } }, 160);
      });
      item.addEventListener('focusout', function (e) {
        var next = e.relatedTarget;
        if (next && item.contains(next)) { return; }
        if (openItem === item) { setOpen(item, false); }
      });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          if (openItem === item) { e.preventDefault(); e.stopPropagation(); setOpen(item, false); btn.focus(); }
        } else if ((e.key === 'ArrowDown' || e.key === 'Down') && e.target === btn) {
          e.preventDefault();
          setOpen(item, true);
          var first = panel.querySelector('a, button');
          if (first) { first.focus(); }
        }
      });
    });

    doc.addEventListener('click', function (e) {
      if (openItem && !openItem.contains(e.target)) { closeAll(); }
    });
    onMQChange(desktopMQ, function (e) {
      closeAll();
      if (e.matches && drawer && hp()) { hp().closeModal(drawer); }
    });

    /* ------------------------------------------------------- 3. drawer bits */
    /* Both overlays live inside the sticky section wrapper (z-index 52 stacking context), so their
       z-index 10000 is local: legacy fixed bars (PDP sticky add-to-cart 1000, scroll-up arrow 100,
       mini-cart 9998/9999) would paint above them. html.hp-header-modal-open lets the section's
       sticky <style> raise the wrapper to 10001 and hp-header.css hide the Inbox launcher. */
    function syncModalClass() {
      var open = (searchModal && searchModal.hasAttribute('open')) || (drawer && drawer.hasAttribute('open'));
      doc.documentElement.classList.toggle('hp-header-modal-open', !!open);
    }
    [searchModal, drawer].forEach(function (modal) {
      if (!modal) { return; }
      modal.addEventListener('hp:modal:open', syncModalClass);
      modal.addEventListener('hp:modal:close', syncModalClass);
    });

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || typeof t.closest !== 'function') { return; }

      var searchBtn = t.closest('[data-hp-drawer-search]');
      if (searchBtn) {
        e.preventDefault();
        var api = hp();
        if (api && searchModal) {
          if (drawer) { api.closeModal(drawer); }
          api.openModal(searchModal, burger || searchBtn);
        } else if (searchModal) {
          var input = searchModal.querySelector('input[type="search"]');
          searchModal.setAttribute('open', '');
          if (input) { input.focus(); }
        }
        return;
      }

      var cartBtn = t.closest('[data-hp-drawer-cart]');
      if (cartBtn && drawer && hp()) {
        /* mini-cart-header.js opens the drawer on the same click (document listener) */
        hp().closeModal(drawer);
        return;
      }

      var acc = t.closest('[data-hp-drawer-toggle]');
      if (acc) {
        e.preventDefault();
        var expanded = acc.getAttribute('aria-expanded') === 'true';
        var sub = doc.getElementById(acc.getAttribute('aria-controls'));
        acc.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (sub) { sub.hidden = expanded; }
      }
    });

    /* --------------------------------------- 4. exact-match decoration */
    var results = root.querySelector('[data-hp-results]');
    var searchInput = root.querySelector('[data-hp-search-input]');
    var exactLabel = root.getAttribute('data-exact-label') || 'Exact match';
    /* role=status live region next to the listbox: predictive-search.js never announces the result
       set, so the group headings ("Products (5), Collections (2)") are mirrored there on every
       change of the container; '' once the wrapper is gone (closed / no results). */
    var resultsStatus = root.querySelector('[data-hp-results-status]');
    var HEADING_SEL = '.predictive-search-products__wrapper > div:first-child > span, ' +
      '.predictive-search-collections > div:first-child > span, ' +
      '.predictive-search-articles > div:first-child > span';
    function announceResults() {
      if (!resultsStatus || !results) { return; }
      var wrap = results.querySelector('.predictive-search-wrapper');
      var parts = [];
      if (wrap) {
        Array.prototype.forEach.call(wrap.querySelectorAll(HEADING_SEL), function (el) {
          var text = String(el.textContent || '').replace(/\s+/g, ' ').trim();
          if (text) { parts.push(text); }
        });
      }
      var next = parts.join(', ');
      if (resultsStatus.textContent !== next) { resultsStatus.textContent = next; }
    }

    function decorateResults() {
      if (!results || !searchInput) { return; }
      var wrap = results.querySelector('.predictive-search-products__wrapper');
      if (!wrap || wrap.__hpDecorated) { return; }
      wrap.__hpDecorated = true;
      var q = norm(searchInput.value);
      if (!q) { return; }
      /* a whole-word match only counts as "exact" for part-number style terms (has a digit, 4+ chars);
         separators are ignored so A06B6079H106 still matches the A06B-6079-H106 row (L6-8 retry) */
      var partLike = q.length >= 4 && /\d/.test(q) && q.indexOf(' ') === -1;
      var qc = compact(q);
      var rows = Array.prototype.slice.call(wrap.querySelectorAll('ul > li'));
      var matches = rows.filter(function (li) {
        var titleEl = li.querySelector('a > span');
        var title = norm(titleEl ? titleEl.textContent : '');
        if (!title) { return false; }
        if (title === q) { return true; }
        if (!partLike) { return false; }
        var toks = title.split(' ');
        for (var k = 0; k < toks.length; k++) {
          if (toks[k] === q || compact(toks[k]) === qc) { return true; }
        }
        return false;
      });
      if (!matches.length) { return; }
      wrap.classList.add('hp-is-exact');
      rows.forEach(function (li) { li.classList.remove('hp-is-last'); if (matches.indexOf(li) === -1) { li.classList.add('hp-is-hidden'); } });
      matches[matches.length - 1].classList.add('hp-is-last');
      var head = wrap.firstElementChild;
      var label = head ? head.querySelector('span') : null;
      if (label) {
        var textNode = null;
        for (var i = 0; i < label.childNodes.length; i++) {
          var n = label.childNodes[i];
          if (n.nodeType === 3 && n.textContent.trim()) { textNode = n; break; }
        }
        if (textNode) { textNode.textContent = exactLabel + ' '; }
        var count = label.querySelector('span');
        if (count) { count.textContent = '(' + matches.length + ')'; }
      }
    }
    /* Combobox pattern: the input declares role=combobox / aria-autocomplete=list / aria-controls the
       role=listbox container, but the theme's predictive-search.js only fills it with li[role=option] > a
       rows plus heading "View all" links and never handles arrow keys. Normalise every inserted
       .predictive-search-wrapper (each result <a> becomes the option, wrapping li becomes role=none)
       and drive ArrowUp/ArrowDown/Enter + aria-activedescendant from here. */
    var optionBase = (searchInput && searchInput.id ? searchInput.id : root.id + '-search') + '-opt-';
    var activeOption = null;
    function optionIsVisible(el) {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }
    function visibleOptions() {
      if (!results || !results.classList.contains('open')) { return []; }
      return Array.prototype.slice.call(results.querySelectorAll('[role="option"]')).filter(optionIsVisible);
    }
    function setActiveOption(el) {
      if (activeOption && activeOption !== el) {
        activeOption.classList.remove('is-active');
        activeOption.setAttribute('aria-selected', 'false');
      }
      activeOption = el || null;
      if (!searchInput) { return; }
      if (activeOption) {
        activeOption.classList.add('is-active');
        activeOption.setAttribute('aria-selected', 'true');
        searchInput.setAttribute('aria-activedescendant', activeOption.id);
        if (typeof activeOption.scrollIntoView === 'function') {
          try { activeOption.scrollIntoView({ block: 'nearest' }); } catch (err) { /* noop */ }
        }
      } else {
        searchInput.removeAttribute('aria-activedescendant');
      }
    }
    function normalizeResults() {
      if (!results) { return; }
      var wrap = results.querySelector('.predictive-search-wrapper');
      if (!wrap || !wrap.contains(activeOption)) { setActiveOption(null); }
      if (!wrap || wrap.__hpNormalized) { return; }
      wrap.__hpNormalized = true;
      Array.prototype.forEach.call(wrap.querySelectorAll('li[role="option"]'), function (li) { li.setAttribute('role', 'none'); });
      Array.prototype.forEach.call(wrap.querySelectorAll('a[href]'), function (a, i) {
        a.setAttribute('role', 'option');
        a.setAttribute('tabindex', '-1');
        a.setAttribute('aria-selected', 'false');
        a.id = optionBase + i;
      });
    }
    if (searchInput) {
      searchInput.addEventListener('keydown', function (e) {
        var key = e.key;
        if (key === 'ArrowDown' || key === 'Down' || key === 'ArrowUp' || key === 'Up') {
          var list = visibleOptions();
          if (!list.length) { return; }
          e.preventDefault();
          var idx = list.indexOf(activeOption);
          if (key === 'ArrowDown' || key === 'Down') {
            idx = idx + 1 >= list.length ? 0 : idx + 1;
          } else {
            idx = idx <= 0 ? list.length - 1 : idx - 1;
          }
          setActiveOption(list[idx]);
        } else if (key === 'Enter') {
          if (activeOption && optionIsVisible(activeOption) && activeOption.href) {
            e.preventDefault();
            win.location.href = activeOption.href;
          }
        }
      });
      /* typing changes the result set; the observer re-syncs once the new wrapper lands */
      searchInput.addEventListener('input', function () { setActiveOption(null); });
    }
    if (results && 'MutationObserver' in win) {
      var mo = new win.MutationObserver(function () { normalizeResults(); decorateResults(); announceResults(); });
      mo.observe(results, { childList: true });
    }
    /* The theme's <predictive-search> (assets/predictive-search.js) closes itself on any document
       click outside the element and its close() calls this.hideLoading(), a method the class never
       defines: once results were shown, the first click anywhere (overlay, close button, the page
       after Escape) throws "this.hideLoading is not a function". Give every instance in this header a
       no-op so its own close path runs cleanly, and close it when the overlay closes so no stale
       results / aria-expanded state is left behind. Own properties survive the custom-element upgrade,
       so this is safe whether or not predictive-search.js has run yet. */
    var predictive = Array.prototype.slice.call(root.querySelectorAll('predictive-search'));
    predictive.forEach(function (el) {
      if (typeof el.hideLoading !== 'function') { el.hideLoading = function () {}; }
    });
    if (searchModal && predictive.length) {
      searchModal.addEventListener('hp:modal:close', function () {
        setActiveOption(null);
        if (resultsStatus) { resultsStatus.textContent = ''; }
        predictive.forEach(function (el) {
          if (typeof el.close === 'function') { try { el.close(); } catch (err) { /* noop */ } }
        });
      });
    }
    /* when the overlay opens, re-run the predictive search for a pre-filled term */
    if (searchModal && searchInput) {
      searchModal.addEventListener('hp:modal:open', function () {
        if (searchInput.value.trim()) {
          try { searchInput.dispatchEvent(new win.Event('input', { bubbles: true })); } catch (err) { /* noop */ }
        }
      });
    }

    /* ---------------------------------------------------- 5. cart count */
    var counters = Array.prototype.slice.call(root.querySelectorAll('[data-hp-cart-count]'));
    function setCount(n) {
      counters.forEach(function (el) { el.textContent = String(n); });
    }
    function refreshCount() {
      var p = null;
      if (win.cart && typeof win.cart.getCart === 'function') {
        p = win.cart.getCart();
      } else if (typeof win.fetch === 'function') {
        p = win.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      }
      if (p && typeof p.then === 'function') {
        p.then(function (cart) { if (cart && typeof cart.item_count === 'number') { setCount(cart.item_count); } }).catch(function () { /* noop */ });
      }
    }
    if (counters.length) {
      doc.addEventListener('cart:updated', function (e) {
        var cart = e && e.detail && e.detail.cart;
        if (cart && typeof cart.item_count === 'number') { setCount(cart.item_count); } else { refreshCount(); }
      });
      doc.addEventListener('cart:item-added', function () { refreshCount(); });
    }
  }

  function boot(scope) {
    var roots = (scope && scope.nodeType === 1 ? scope : doc).querySelectorAll('[data-hp-header]');
    Array.prototype.forEach.call(roots, initHeader);
  }

  ready(function () { boot(doc); });
  doc.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})(window, document);
